import React from "react";
import { Helmet } from "react-helmet-async";
import rest from "../RESTDATA";
import {
  getLanguageFromPathname,
  getLocalizedPath,
  stripLanguageFromPathname,
  SUPPORTED_LANGUAGES,
} from "../locales/routing";

const SITE_URL = "https://dusha.bbqr.site";
const DEFAULT_TITLE = "Dusha Beach Restaurant";
const DEFAULT_DESCRIPTION = "Dusha beach Bar Restaurant Nea Kallikratia";
const DEFAULT_IMAGE = "/img/hero-bg1.avif";
const LANG_TO_HTML = { ua: "uk", se: "sr" };
const LANG_TO_OG_LOCALE = {
  en: "en_US",
  de: "de_DE",
  ru: "ru_RU",
  el: "el_GR",
  ua: "uk_UA",
  uk: "uk_UA",
  se: "sr_RS",
  sr: "sr_RS",
};

function toAbsoluteUrl(value = "") {
  if (!value) {
    return SITE_URL;
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  return value.startsWith("/") ? `${SITE_URL}${value}` : `${SITE_URL}/${value}`;
}

function toSeoLang(lang = "en") {
  return LANG_TO_HTML[lang] || lang;
}

function toOgLocale(lang = "en") {
  return LANG_TO_OG_LOCALE[lang] || "en_US";
}

export default function Seo({
  title,
  description,
  pathname,
  image,
  canonical,
  children,
  lang = "en",
}) {
  const pathWithoutLang = stripLanguageFromPathname(pathname || "/");
  const langFromPathname = getLanguageFromPathname(pathname || "/");
  const resolvedLang = langFromPathname || lang;
  const localizedPathname = getLocalizedPath(resolvedLang, pathname || "/");
  const seoLang = toSeoLang(resolvedLang);
  const metaTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const metaUrl = canonical
    ? toAbsoluteUrl(canonical)
    : toAbsoluteUrl(localizedPathname);
  const metaImage = image ? toAbsoluteUrl(image) : toAbsoluteUrl(DEFAULT_IMAGE);
  const alternateLinks = SUPPORTED_LANGUAGES.map((currentLanguage) => ({
    hrefLang: toSeoLang(currentLanguage),
    href: toAbsoluteUrl(getLocalizedPath(currentLanguage, pathWithoutLang)),
  }));
  const defaultLanguageUrl = toAbsoluteUrl(getLocalizedPath("en", pathWithoutLang));
  const restSpot = rest?.coords?.restSpot;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: DEFAULT_TITLE,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    image: [metaImage],
    telephone: rest?.tel,
    email: rest?.email,
    servesCuisine: ["Greek", "Mediterranean"],
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: rest?.address,
      addressLocality: "Nea Kallikratia",
      addressRegion: "Halkidiki",
      addressCountry: "GR",
    },
    geo: restSpot
      ? {
          "@type": "GeoCoordinates",
          latitude: restSpot.latitude,
          longitude: restSpot.longitude,
        }
      : undefined,
    sameAs: [
      "https://www.facebook.com/dusha.halkidiki/",
      "https://www.instagram.com/dusha.halkidiki/",
    ],
  };

  return (
    <Helmet>
      <html lang={seoLang} />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={metaUrl} />
      {alternateLinks.map((alternateLink) => (
        <link
          key={alternateLink.hrefLang}
          rel="alternate"
          hrefLang={alternateLink.hrefLang}
          href={alternateLink.href}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={defaultLanguageUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={DEFAULT_TITLE} />
      <meta property="og:locale" content={toOgLocale(seoLang)} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:image" content={metaImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {children}
    </Helmet>
  );
}
